import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  MotifFormField,
  MotifIcon,
  MotifSearch,
  MotifTabControl,
  MotifTabNavigation,
  MotifTooltip,
} from '@ey-xd/motif-react'

import NoDataImage from '../../assets/images/no_data_light.svg'
import {
  useLazyListEngagementQuery,
  useSearchEngagementsMutation,
} from '../../features/Client/clientApi'
import EngagementCard, { ClientEngagementModal } from '@/features/Client'
import type { EngagementCardData } from '../../services/types'
import styles from './index.module.scss'
import { IconoirInfoCircle } from '@ey-xd/motif-icon'

const NoRecordsFound: React.FC = () => (
  <div className={styles.no_records_found}>
    <img src={NoDataImage} alt="No Data" className={styles.no_records_img} />
    <br />
    <div className={styles.no_records}>
      <div className={styles.title}>No Client cards found</div>
      <div className={styles.text}>Create client card to proceed further</div>
    </div>
  </div>
)

const EngagementList: React.FC<{
  engagments: EngagementCardData[]
  isArchived: boolean
}> = ({ engagments }) => (
  <div className="motif-container">
    <div className="motif-row">
      {engagments.map((engagement, index) => (
        <div
          className={`motif-col-sm-6 motif-col-md-4 motif-col-lg-3 ${styles.mb15px}`}
          key={index}
        >
          <EngagementCard engagement={engagement} />
        </div>
      ))}
    </div>
  </div>
)

const enum TabIndex {
  ActiveEngagements = 0,
  ArchivedEngagements = 1,
}

export const ClientCardContainer: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  const currentEngagements = useSelector(
    (state: { clientCard: { clients: EngagementCardData[] } }) =>
      state.clientCard.clients,
  )
  const engagementsLoading = useSelector(
    (state: { clientCard: { loading: boolean } }) => state.clientCard.loading,
  )
  const [listEngagements] = useLazyListEngagementQuery()
  const [searchEngagements] = useSearchEngagementsMutation()

  useEffect(() => {
    listEngagements(activeTab === TabIndex.ArchivedEngagements)
  }, [activeTab, listEngagements])

  const handleTabChange = (tabIndex: number) => {
    if (activeTab !== tabIndex) {
      setActiveTab(tabIndex)
      setSearchQuery('')
    }
  }

  const refreshEngagements = () => {
    listEngagements(activeTab === TabIndex.ArchivedEngagements)
  }

  const handleSearch = () => {
    searchEngagements({
      query: searchQuery,
      isArchived: activeTab === 1,
    })
  }

  const handleClearSearch = () => {
    searchEngagements({
      query: "",
      isArchived: activeTab === 1,
    })
  }

  return (
    <>
      <MotifTabNavigation
        defaultActiveKey={0}
        orientation="horizontal"
        size="sm"
        variant="default"
      >
        <MotifTabControl
          tabKey={0}
          onClick={() => handleTabChange(TabIndex.ActiveEngagements)}
        >
          Active Engagements
          <MotifTooltip
            allowHover
            hideCloseButton
            flip
            forceHideCounter={0}
            id="tooltip"
            offset={[0, 8]}
            placement="top"
            style={{ marginLeft: '10px' }}
            trigger={
              <MotifIcon
                iconFunction={IconoirInfoCircle}
                title="Active Engagements"
                className="margin-left-5"
              />
            }
            usePortal
            variant="default"
          >
            <div className="motif-body2-default-bold">Active Engagements</div>
            <div className="motif-caption-default-light motif-tooltip-custom-width">
              Active engagements are those still visible because their report date has not yet passed.
            </div>
          </MotifTooltip>
        </MotifTabControl>
        <MotifTabControl
          tabKey={1}
          onClick={() => handleTabChange(TabIndex.ArchivedEngagements)}
        >
          Archived
          <MotifTooltip
            contentClassName="custom-tooltip"
            allowHover
            hideCloseButton
            flip
            forceHideCounter={0}
            id="tooltip"
            offset={[0, 8]}
            placement="top"
            style={{ marginLeft: '10px' }}
            trigger={
              <MotifIcon
                iconFunction={IconoirInfoCircle}
                title="Active Engagements"
                className="margin-left-5"
              />
            }
            usePortal
            variant="default"
          >
            <div className="motif-body2-default-bold">Archived</div>
            <div className="motif-caption-default-light motif-tooltip-custom-width">
              Archived engagements are those for which the report date has passed, and the associated data has been purged.
            </div>
          </MotifTooltip>
        </MotifTabControl>
      </MotifTabNavigation>
      <div className={styles.tabPanelSearch}>
        <MotifFormField>
          <MotifSearch
            aria-label="Search"
            id="engagement-search"
            items={[]}
            onChange={(event) => setSearchQuery(event.target.value)}
            onSelect={() => { }}
            placeholder="Search"
            value=""
            onEnter={handleSearch}
            onClear={handleClearSearch}
          />
        </MotifFormField>
      </div>
      <div className="tab-panel-content pad-t-20">
        {engagementsLoading || !currentEngagements ? (
          <NoRecordsFound />
        ) : (
          <EngagementList
            engagments={Object.values(currentEngagements)}
            isArchived={activeTab === 1 ? true : false}
          />
        )}
      </div>
      <ClientEngagementModal
        refreshEngagements={refreshEngagements}
      />
    </>
  )
}
